/**
 * fieldLabels.ts
 * Registre centralisé : nom de champ DB → libellé français.
 * Utilisé par MyTable (DetailDialog), les exports, les formulaires, etc.
 *
 * Structure :
 *   COMMON_LABELS   — champs partagés par tous les modèles
 *   MODEL_LABELS    — champs spécifiques par modèle (merge avec COMMON_LABELS)
 *   HIDDEN_FIELDS   — champs techniques toujours masqués dans les dialogs
 *   getLabels(model)— helper pour récupérer le merge final
 */

import { labels } from './labels';

export type FieldLabelMap = Record<string, string>;

// ── Champs communs à tous les modèles ────────────────────────────────────────

export const COMMON_LABELS: FieldLabelMap = {
  id:                'ID',
  owner_id:          'Propriétaire (ID)',
  user_id:           'Utilisateur (ID)',
  departement_id:    'Département (ID)',
  created_at:        'Créé le',
  updated_at:        'Modifié le',
  date_creation:     'Date de création',
  date_modification: 'Date de modification',
  visible:           'Visible',
  nom:               'Nom',
  description:       'Description',
};

// ── Champs techniques à masquer par défaut dans les dialogs ──────────────────

export const HIDDEN_FIELDS: string[] = [
  'owner_id',
  'user_id',
  'type_donnee_id',
  'portee', // métadonnée technique du type (GLOBAL/DEPARTEMENT), pas affichée en détail
  // Partage par département (0130) : techniques — le libellé lisible est departements_partage_noms
  'dept_restreint',
  'departements_partage_ids',
  // Prix bruts de la fiche article : réservés au formulaire d'édition, NULL en mode département (0153)
  'prix_r_defaut',
  'prix_v_defaut',
  'remise_defaut',
];

// ── Labels par modèle ─────────────────────────────────────────────────────────

const TIERS_LABELS: FieldLabelMap = {
  nom:           'Nom',
  prenom:        'Prénom',
  email:         'Email',
  telephone:     'Téléphone',
  adresse:       'Adresse',
  type:          'Type de tiers',
  categorie_id:  'Catégorie (ID)',
  categorie_nom: 'Catégorie',
  departements_partage_noms: 'Départements',
};

const ARTICLE_LABELS: FieldLabelMap = {
  code:              'Code',
  designation:       'Désignation',
  prix_r:            "Prix d'achat",
  prix_v:            'Prix de vente',
  tva:               'TVA (%)',
  remise:            'Remise (%)',
  poids:             'Poids',
  unite:             'Unité',
  unite_sec:         'Unité secondaire',
  stock_securite:    'Stock de sécurité',
  track_stock:       'Suivi du stock',
  allow_pcs_cts:     'Pièces / cartons',
  pieces_par_carton: 'Pièces par carton',
  famille_id:        'Famille (ID)',
  famille_nom:       'Famille',
  fournisseur_id:    `${labels.fournisseur} (ID)`,
  fournisseur_nom:   labels.fournisseur,
  departements_partage_noms: 'Départements',
};

const REGLEMENT_LABELS: FieldLabelMap = {
  numero_reglement: 'Numéro',
  date_reglement:   'Date',
  montant:          'Montant',
  sens:             'Sens',
  type_reglement:   'Type',
  reference:        'Référence',
  tiers_id:         'Tiers (ID)',
  caisse_id:        'Caisse (ID)',
  bien_id:          'Bien (ID)',
  mode_paiement_id: 'Mode de paiement (ID)',
  nature_id:        'Nature (ID)',
  extra_info:       'Informations extra',
  departement_nom:  'Département',
};

const DOCUMENT_VENTE_LABELS: FieldLabelMap = {
  numero_doc:      'Numéro document',
  date_doc:        'Date document',
  date_echeance:   'Date échéance',
  cloture:         'Clôturé',
  reference:       'Référence',
  total_ht:        'Total HT',
  total_ttc:       'Total TTC',
  tiers_id:        'Tiers (ID)',
  depot_id:        'Dépôt (ID)',
  contrat_id:      'Contrat (ID)',
  type_doc_id:     'Type document (ID)',
  doc_code:        'Code document',
  related_doc:     'Document lié (ID)',
  departement_nom: 'Département',
};

const CONTRAT_LOCATION_LABELS: FieldLabelMap = {
  numero_contrat:        'Numéro contrat',
  date_debut:            'Date début',
  date_fin:              'Date fin',
  loyer:                 'Loyer',
  charge:                'Charges',
  frais_enregistrement:  "Frais d'enregistrement",
  depot_garantie:        'Dépôt de garantie',
  revision_loyer:        'Révision loyer',
  enregistre:            'Enregistré',
  etat:                  'État',
  locataire_id:          'Locataire (ID)',
  bien_id:               'Bien (ID)',
  creeFacture:           'Facture créée',
};

const BIEN_LABELS: FieldLabelMap = {
  nom:            'Nom',
  type:           'Type',
  adresse:        'Adresse',
  surface:        'Surface (m²)',
  loyer:          'Loyer',
  impot_foncier:  'Impôt foncier',
  isDisponible:   'Disponible',
  proprietaire_id:'Propriétaire (ID)',
  groupe_bien_id: 'Groupe (ID)',
};

const AI_API_TOKEN_LABELS: FieldLabelMap = {
  nom:                  'Nom',
  login:                'Généré par',
  actif:                'Actif',
  derniere_utilisation: 'Dernière utilisation',
};

const DEPARTEMENT_LABELS: FieldLabelMap = {
  nom:        'Nom',
  description:'Description',
  depot_id:   'Dépôt (ID)',
  depot_nom:  'Dépôt',
};

const GENERIQUE_DATA_LABELS: FieldLabelMap = {
  type_donnee: 'Type',
  nom:         'Nom',
  description: 'Description',
};

const USER_LABELS: FieldLabelMap = {
  login:           'Login',
  email:           'Email',
  phone:           'Téléphone',
  is_active:       'Actif',
  is_valide_email: 'Email validé',
  roles_noms:      'Rôle(s)',
};

const REPORT_TEMPLATE_LABELS: FieldLabelMap = {
  nom:                'Nom',
  type_rapport:       'Type de rapport',
  type_document_code: 'Code document',
  is_default:         'Template par défaut',
};

const REPORT_JOB_LABELS: FieldLabelMap = {
  type_rapport: 'Type de rapport',
  format:       'Format',
  statut:       'Statut',
  template_nom: 'Template',
  file_path:    'Fichier',
  error_message:'Message d\'erreur',
  parametres:   'Paramètres',
};

const WORKFLOW_ETAPE_LABELS: FieldLabelMap = {
  workflow_id:      'Workflow (ID)',
  workflow_nom:     'Workflow',
  doc_source_code:  'Document source',
  doc_cible_code:   'Document cible',
  source_libelle:   'Libellé source',
  cible_libelle:    'Libellé cible',
  ordre:            'Ordre',
};

const STOCK_ARTICLE_LABELS: FieldLabelMap = {
  ...ARTICLE_LABELS,
  stock_reel:      'Stock réel',
  total_entrees:   'Total entrées',
  total_sorties:   'Total sorties',
  valorisation_achat: 'Valorisation (achat)',
  valorisation_vente: 'Valorisation (vente HT)',
  valorisation_vente_ttc: 'Valorisation (vente TTC)',
  famille_parent_id: 'Famille parente (ID)',
};

const STOCK_MOUVEMENT_LABELS: FieldLabelMap = {
  date_doc:           'Date',
  numero_doc:         'N° doc',
  type_doc_code:      'Type doc',
  type_doc_libelle:   'Libellé type doc',
  article_id:         'Article (ID)',
  code:               'Code',
  designation:        'Désignation',
  unite:              'Unité',
  famille_id:         'Famille (ID)',
  famille_nom:        'Famille',
  depot_id:           'Dépôt (ID)',
  depot_nom:          'Dépôt',
  departement_id:     'Département (ID)',
  departement_nom:    'Département',
  document_vente_id:  'Document (ID)',
  quantite:           'Quantité',
  signe:              'Signe',
  quantite_signee:    'Qté signée',
  valeur_mouvement:   'Valeur',
  tiers_id:           'Tiers (ID)',
  tiers_nom:          'Tiers',
  user_login:         'Utilisateur',
  stock_cumule:       'Stock cumulé',
};

const TIERS_PLAFOND_LABELS: FieldLabelMap = {
  tiers_id:        'Tiers (ID)',
  departement_id:  'Département (ID)',
  departement_nom: 'Département',
  plafond:         'Plafond',
};

const COMPTE_LABELS: FieldLabelMap = {
  date:             'Date',
  numero_operation: "Numéro d'opération",
  reference:        'Référence',
  debit:            'Débit',
  credit:           'Crédit',
  solde:            'Solde',
  montant:          'Montant',
  statut:           'Statut',
  type_compte:      'Type de compte',
  type_operation:   'Type opération',
  sens:             'Sens',
  departement_id:   'Département (ID)',
  departement_nom:  'Département',
};

const BALANCE_COMPTE_LABELS: FieldLabelMap = {
  tiers_id:        'Tiers (ID)',
  nom:             'Nom',
  prenom:          'Prénom',
  telephone:       'Téléphone',
  type_tiers:      'Type',
  solde_anterieur: 'Solde antérieur',
  total_facture:   'Total Facture',
  total_reglement: 'Total Règlement',
  solde_final:     'Solde',
};

const WORKFLOW_STATUT_LABELS: FieldLabelMap = {
  code:           'Code',
  libelle:        'Libellé',
  couleur:        'Couleur',
  icone:          'Icône',
  ordre:          'Ordre',
  is_initial:     'Statut initial',
  is_final:       'Statut final',
  doc_type_code:  'Type de document',
  departement_id: 'Département',
  visible:        'Visible',
};

const IMMOBILISATION_LABELS: FieldLabelMap = {
  libelle:             'Libellé',
  valeur_acquisition:  "Valeur d'acquisition",
  date_acquisition:    "Date d'acquisition",
  duree_mois:          'Durée (mois)',
  methode:             'Méthode',
  date_cession:        'Date de cession',
  valeur_cession:      'Valeur de cession',
  document_vente_id:   'Document (ID)',
  ligne_document_id:   'Ligne document (ID)',
  amortissement_cumule:'Amortissement cumulé',
  vnc:                 'Valeur nette comptable',
};

const WORKFLOW_STATUT_TRANSITION_LABELS: FieldLabelMap = {
  doc_type_code:         'Type de document',
  statut_source_id:      'Statut source (id)',
  statut_source_code:    'Statut source',
  statut_source_libelle: 'Libellé source',
  statut_cible_id:       'Statut cible (id)',
  statut_cible_code:     'Statut cible',
  statut_cible_libelle:  'Libellé cible',
  libelle_action:        'Libellé bouton',
  permission_code:       'Permission requise',
  auto_trigger:          'Déclencheur automatique',
  departement_id:        'Département',
};

// ── Registre global ───────────────────────────────────────────────────────────

// Aperçu des actions de masse par famille (migration 0134)
const ARTICLE_MASSE_APERCU_LABELS: FieldLabelMap = {
  article_id:           'Article (ID)',
  code:                 'Code',
  designation:          'Désignation',
  famille_nom:          'Famille',
  avant:                'Avant',
  apres:                'Après',
  ligne_dept_existante: 'Prix département existant',
  invalide:             'Valeur invalide',
};

const ARTICLE_MASSE_FAMILLE_LABELS: FieldLabelMap = {
  article_id:           'Article (ID)',
  code:                 'Code',
  designation:          'Désignation',
  famille_nom:          'Famille actuelle',
  nouvelle_famille_nom: 'Nouvelle famille',
};

// Aperçu de l'affectation de fournisseur (migration 0143)
const ARTICLE_MASSE_FOURNISSEUR_LABELS: FieldLabelMap = {
  article_id:              'Article (ID)',
  code:                    'Code',
  designation:             'Désignation',
  famille_nom:             'Famille',
  fournisseur_nom:         labels.fournisseurActuel,
  nouveau_fournisseur_nom: labels.nouveauFournisseur,
};

const ARTICLE_MASSE_PARTAGE_LABELS: FieldLabelMap = {
  article_id:  'Article (ID)',
  code:        'Code',
  designation: 'Désignation',
  famille_nom: 'Famille',
  avant_noms:  'Partage actuel',
  apres_noms:  'Nouveau partage',
  change:      'Modifié',
  invalide:    'Valeur invalide',
};

const ARTICLE_MASSE_VALEURS_LABELS: FieldLabelMap = {
  article_id:    'Article (ID)',
  code:          'Code',
  designation:   'Désignation',
  famille_nom:   'Famille',
  modifications: 'Modifications',
  change:        'Modifié',
};

// Valeurs par défaut d'une famille (migration 0136)
const FAMILLE_DEFAUT_LABELS: FieldLabelMap = {
  famille_id:        'Famille (ID)',
  departement_id:    'Département (ID)',
  departement_nom:   'Département',
  prix_v:            'Prix de vente',
  prix_r:            'Prix de revient',
  remise:            'Remise max (%)',
  tva:               'TVA (%)',
  unite:             'Unité',
  unite_sec:         'Unité secondaire',
  pieces_par_carton: 'Pièces par carton',
  track_stock:       'Suivi du stock',
  stock_securite:    'Stock de sécurité',
  fournisseur_id:    `${labels.fournisseur} (ID)`,
  fournisseur_nom:   labels.fournisseur,
};

export const MODEL_LABELS: Record<string, FieldLabelMap> = {
  Tiers:                        TIERS_LABELS,
  Article:                      ARTICLE_LABELS,
  ArticleMasseApercuLigne:      ARTICLE_MASSE_APERCU_LABELS,
  ArticleMasseFamilleLigne:     ARTICLE_MASSE_FAMILLE_LABELS,
  ArticleMasseFournisseurLigne: ARTICLE_MASSE_FOURNISSEUR_LABELS,
  ArticleMassePartageLigne:     ARTICLE_MASSE_PARTAGE_LABELS,
  ArticleMasseValeursLigne:     ARTICLE_MASSE_VALEURS_LABELS,
  FamilleDefaut:                FAMILLE_DEFAUT_LABELS,
  StockArticle:                 STOCK_ARTICLE_LABELS,
  StockMouvement:               STOCK_MOUVEMENT_LABELS,
  Reglement:                    REGLEMENT_LABELS,
  Document_vente:               DOCUMENT_VENTE_LABELS,
  ContratLocation:              CONTRAT_LOCATION_LABELS,
  Bien:                         BIEN_LABELS,
  Departement:                  DEPARTEMENT_LABELS,
  GeneriqueData:                GENERIQUE_DATA_LABELS,
  User:                         USER_LABELS,
  ReportTemplate:               REPORT_TEMPLATE_LABELS,
  ReportJob:                    REPORT_JOB_LABELS,
  WorkflowEtape:                WORKFLOW_ETAPE_LABELS,
  Compte:                       COMPTE_LABELS,
  BalanceCompte:                BALANCE_COMPTE_LABELS,
  TiersPlafond:                 TIERS_PLAFOND_LABELS,
  WorkflowStatut:               WORKFLOW_STATUT_LABELS,
  WorkflowStatutTransition:     WORKFLOW_STATUT_TRANSITION_LABELS,
  Immobilisation:               IMMOBILISATION_LABELS,
  AiApiToken:                   AI_API_TOKEN_LABELS,
};

/**
 * Retourne le map de labels fusionné (COMMON + modèle spécifique).
 * Utilisation : getLabels('Reglement')
 */
export const getLabels = (modelName: keyof typeof MODEL_LABELS): FieldLabelMap => ({
  ...COMMON_LABELS,
  ...(MODEL_LABELS[modelName] ?? {}),
});

/**
 * Retourne le label d'un champ, avec fallback sur le nom brut.
 */
export const getFieldLabel = (key: string, labels: FieldLabelMap): string =>
  labels[key] ?? key;
